import { Delete, Edit } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';
import { Collapse, Divider, IconButton, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { Roles } from 'enums';
import PaiPagination from 'features/ui/base-components/pai-pagination';
import PaiSpinningLoader from 'features/ui/base-components/pai-spinning-loader';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiMenuItem from 'features/ui/base-components/pai-menu-item';
import PaiRadioGroup from 'features/ui/base-components/pai-radio-group';
import PaiRoundTextField from 'features/ui/base-components/pai-round-text-field';
import PaiSelect from 'features/ui/base-components/pai-select';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { formatDateTime } from 'utils/date.utils';
import { Company } from '../../../types/company.types';
import { License } from '../../../types/licenses.types';
import { CompanySearchTypes, TenantScopes, TenantSortBy, User } from './account-management.types';
import TenantLicensesTable from './tenants/tenant-license-table';
import TenantUsersTable from './tenants/tenant-users-table';

const TenantItemSkeleton: React.FC = () => {
  const { classes, cx } = useStyles();
  return (
    <div className={cx(classes.tenantItem, 'skeleton')}>
      <Stack className={classes.tenantItemHeader} direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Skeleton variant="circular" width={24} height={24} />
          <div>
            <Skeleton variant="text" width={200} height={21} />
            <Skeleton variant="text" width={200} height={18} />
          </div>
        </Stack>
        <Stack direction={'row'} alignItems={'center'} gap={1}>
          <Skeleton variant="circular" width={28} height={28} />
          <Skeleton variant="circular" width={28} height={28} />
        </Stack>
      </Stack>
    </div>
  );
};

interface ItemProps {
  isRealAdmin: boolean;

  open: boolean;
  tenantScope: TenantScopes;
  tenant: Company;

  tenantUsers: User[];
  tenantUsersTotal: number;
  tenantUsersLoading: boolean;
  tenantUsersItemsPerPage: number;
  tenantUsersPage: number;

  tenantLicenses: License[];
  tenantLicensesTotal: number;
  tenantLicensesLoading: boolean;
  tenantLicensesPaginationLoading: boolean;
  tenantLicensesItemsPerPage: number;
  tenantLicensesPage: number;

  handleOpenTenant(): void;

  handleOpenDeleteTenantDialog(event: React.MouseEvent<HTMLButtonElement>): void;

  handleOpenEditTenantDialog(event: React.MouseEvent<HTMLButtonElement>): void;

  tenantUsersHandleChangePage(event: unknown, newPage: number): void;

  handleOpenCreateUserDialog(): void;

  handleOpenEditUserDialog(user: User): () => void;

  handleOpenDeleteUserDialog(user: User): () => void;

  handleOpenAdminSudoLoginDialog(user: User): () => void;

  tenantLicensesHandleChangePage(event: unknown, newPage: number): void;

  handleOpenAdminCreateLicenseDialog(tenantId: string): () => void;

  handleOpenAdminEditLicenseDialog(tenantId: string): (license: License) => () => void;

  handleOpenDeleteLicenseDialog(license: License): () => void;

  handleOpenManageUserLicensesDialog(user: User): () => void;

  handleOpenAgencyDialog(event: React.MouseEvent<HTMLButtonElement>): void;

  handleResendInvitation(user: User): () => void;

  resendingInvitationUserId: string | null;
}

const TenantItem: React.FC<ItemProps> = ({
  isRealAdmin,
  open,
  tenantScope,
  tenant,
  tenantUsers,
  tenantUsersTotal,
  tenantUsersLoading,
  tenantUsersItemsPerPage,
  tenantUsersPage,
  tenantLicenses,
  tenantLicensesTotal,
  tenantLicensesLoading,
  tenantLicensesPaginationLoading,
  tenantLicensesItemsPerPage,
  tenantLicensesPage,
  handleOpenTenant,
  handleOpenDeleteTenantDialog,
  handleOpenEditTenantDialog,
  tenantUsersHandleChangePage,
  handleOpenCreateUserDialog,
  handleOpenEditUserDialog,
  handleOpenDeleteUserDialog,
  handleOpenAdminSudoLoginDialog,
  tenantLicensesHandleChangePage,
  handleOpenAdminCreateLicenseDialog,
  handleOpenAdminEditLicenseDialog,
  handleOpenDeleteLicenseDialog,
  handleOpenManageUserLicensesDialog,
  handleOpenAgencyDialog,
  handleResendInvitation,
  resendingInvitationUserId,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  return (
    <div className={cx(classes.tenantItem, { open })}>
      <Stack
        className={classes.tenantItemHeader}
        direction={'row'}
        alignItems={'center'}
        justifyContent={'space-between'}
        onClick={handleOpenTenant}
        sx={(theme) => ({ borderBottom: open ? `1px solid ${theme.palette.divider}` : undefined })}
      >
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <ExpandMoreIcon className={cx(classes.expandIcon, { open }, 'expand-icon')} />
          <Stack>
            <Stack direction={'row'} alignItems={'center'} gap={1}>
              <Typography className={classes.tenantName}>{tenant.name}</Typography>
              {isRealAdmin && (
                <Tooltip title={tenant.agencyOwnerTenantName ? `${t('agency')}: ${tenant.agencyOwnerTenantName}` : t('bindToAgency') || ''}>
                  <IconButton
                    className={classes.iconButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleOpenAgencyDialog(e);
                    }}
                    size={'small'}
                  >
                    <SupervisedUserCircleIcon className={classes.icon} />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
            <Typography className={classes.tenantDate}>
              {`${t('createdAt')}: ${formatDateTime(new Date(tenant.createdAt).toISOString(), 'Pp')}`}
            </Typography>
          </Stack>
        </Stack>
        <Stack direction={'row'} alignItems={'center'} gap={1}>
          <IconButton className={classes.iconButton} onClick={handleOpenEditTenantDialog}>
            <Edit />
          </IconButton>
          <IconButton className={cx(classes.iconButton, 'delete')} onClick={handleOpenDeleteTenantDialog}>
            <Delete />
          </IconButton>
        </Stack>
      </Stack>
      <Collapse in={open} timeout={'auto'} unmountOnExit={true}>
        <div className={classes.tenantItemContent}>
          {tenantUsersLoading || tenantLicensesLoading ? (
            <PaiSpinningLoader />
          ) : tenantScope === TenantScopes.USERS ? (
            <TenantUsersTable
              tenantUsers={tenantUsers}
              tenantUsersTotal={tenantUsersTotal}
              tenantUsersItemsPerPage={tenantUsersItemsPerPage}
              tenantUsersPage={tenantUsersPage}
              handleOpenCreateUserDialog={handleOpenCreateUserDialog}
              handleOpenEditUserDialog={handleOpenEditUserDialog}
              handleOpenDeleteUserDialog={handleOpenDeleteUserDialog}
              handleOpenAdminSudoLoginDialog={handleOpenAdminSudoLoginDialog}
              tenantUsersHandleChangePage={tenantUsersHandleChangePage}
              handleOpenManageUserLicensesDialog={handleOpenManageUserLicensesDialog}
              handleResendInvitation={handleResendInvitation}
              resendingInvitationUserId={resendingInvitationUserId}
            />
          ) : (
            <TenantLicensesTable
              tenantLicenses={tenantLicenses}
              tenantLicensesTotal={tenantLicensesTotal}
              tenantLicensesPaginationLoading={tenantLicensesPaginationLoading}
              tenantLicensesItemsPerPage={tenantLicensesItemsPerPage}
              tenantLicensesPage={tenantLicensesPage}
              tenantLicensesHandleChangePage={tenantLicensesHandleChangePage}
              handleOpenAdminCreateLicenseDialog={handleOpenAdminCreateLicenseDialog(tenant.id)}
              handleOpenAdminEditLicenseDialog={handleOpenAdminEditLicenseDialog(tenant.id)}
              handleOpenDeleteLicenseDialog={handleOpenDeleteLicenseDialog}
            />
          )}
        </div>
      </Collapse>
    </div>
  );
};

interface Props {
  userRole: Roles;
  tenants: Company[];
  loading: boolean;
  total: number;
  itemsPerPage: number;
  page: number;
  searchParam: string;
  searchType: CompanySearchTypes;
  sortBy: string;

  openTenantId: string | null;
  tenantScope: TenantScopes;
  tenantUsers: User[];
  tenantUsersTotal: number;
  tenantUsersLoading: boolean;
  tenantUsersItemsPerPage: number;
  tenantUsersPage: number;

  tenantLicenses: License[];
  tenantLicensesTotal: number;
  tenantLicensesLoading: boolean;
  tenantLicensesPaginationLoading: boolean;
  tenantLicensesItemsPerPage: number;
  tenantLicensesPage: number;

  handleChangePage(event: unknown, newPage: number): void;

  handleSearchParamChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleClearSearchField(): void;

  handleSearchTypeChange(event: React.ChangeEvent<HTMLInputElement>): void;

  searchKeyDown(): void;

  searchKeyUp(): void;

  handleSortByChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleOpenDeleteTenantDialog(tenant: Company): (event: React.MouseEvent<HTMLButtonElement>) => void;

  handleOpenCreateTenantDialog(): void;

  handleOpenEditTenantDialog(tenant: Company): (event: React.MouseEvent<HTMLButtonElement>) => void;

  handleOpenTenant(tenant: Company): () => void;

  handleToggleTenantScope(): void;

  tenantUsersHandleChangePage(event: unknown, newPage: number): void;

  handleOpenCreateUserDialog(): void;

  handleOpenEditUserDialog(user: User): () => void;

  handleOpenDeleteUserDialog(user: User): () => void;

  handleOpenAdminSudoLoginDialog(user: User): () => void;

  tenantLicensesHandleChangePage(event: unknown, newPage: number): void;

  handleOpenAdminCreateLicenseDialog(tenantId: string): () => void;

  handleOpenAdminEditLicenseDialog(tenantId: string): (license: License) => () => void;

  handleOpenDeleteLicenseDialog(license: License): () => void;

  handleOpenManageUserLicensesDialog(user: User): () => void;

  handleOpenAgencyDialog(tenant: Company): (event: React.MouseEvent<HTMLButtonElement>) => void;

  handleResendInvitation(user: User): () => void;

  resendingInvitationUserId: string | null;
}

const ManageTenants: React.FC<Props> = ({
  userRole,
  tenants,
  loading,
  total,
  itemsPerPage,
  page,
  searchParam,
  searchType,
  sortBy,
  openTenantId,
  tenantScope,
  tenantUsers,
  tenantUsersTotal,
  tenantUsersLoading,
  tenantUsersItemsPerPage,
  tenantUsersPage,
  tenantLicenses,
  tenantLicensesTotal,
  tenantLicensesLoading,
  tenantLicensesPaginationLoading,
  tenantLicensesItemsPerPage,
  tenantLicensesPage,
  handleClearSearchField,
  handleSearchParamChange,
  handleSearchTypeChange,
  searchKeyDown,
  searchKeyUp,
  handleChangePage,
  handleSortByChange,
  handleOpenDeleteTenantDialog,
  handleOpenCreateTenantDialog,
  handleOpenEditTenantDialog,
  handleOpenTenant,
  handleToggleTenantScope,
  tenantUsersHandleChangePage,
  handleOpenCreateUserDialog,
  handleOpenEditUserDialog,
  handleOpenDeleteUserDialog,
  handleOpenAdminSudoLoginDialog,
  tenantLicensesHandleChangePage,
  handleOpenAdminCreateLicenseDialog,
  handleOpenAdminEditLicenseDialog,
  handleOpenDeleteLicenseDialog,
  handleOpenManageUserLicensesDialog,
  handleOpenAgencyDialog,
  handleResendInvitation,
  resendingInvitationUserId,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const tenantsListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    tenantsListRef.current?.scrollTo({ top: 0 });
  }, [page]);

  const isRealAdmin = userRole === Roles.ADMIN || userRole === Roles.OWNER;

  const sortByOptions = [
    { label: t('newestFirst'), value: TenantSortBy.CREATED_AT_DESC },
    { label: t('oldestFirst'), value: TenantSortBy.CREATED_AT_ASC },
    { label: t('nameAscending'), value: TenantSortBy.NAME_ASC },
    { label: t('nameDescending'), value: TenantSortBy.NAME_DESC },
  ];

  if (isRealAdmin) {
    sortByOptions.push({ label: t('newestLicenseFirst'), value: TenantSortBy.LICENSE_DESC });
    sortByOptions.push({ label: t('oldestLicenseFirst'), value: TenantSortBy.LICENSE_ASC });
  }

  return (
    <div className={classes.manageTenants}>
      <Typography className={classes.blockTitle}>{t('tenantListTitle')}</Typography>
      <Stack className={classes.listHeader} direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={2}>
        <Stack direction={'row'} alignItems={'center'} gap={2} flexWrap={'wrap'}>
          <PaiRoundTextField
            value={searchParam}
            variant={'search'}
            placeholder={t('enterSearchTermHere')}
            onClear={handleClearSearchField}
            onChange={handleSearchParamChange}
            onKeyDown={searchKeyDown}
            onKeyUp={searchKeyUp}
            style={{ width: '250px' }}
          />
          <Stack direction={'row'} alignItems={'center'} gap={2}>
            <Typography className={classes.searchTypeLabel}>{t('searchBy')}</Typography>
            <PaiRadioGroup
              items={[
                { label: t('user'), value: CompanySearchTypes.USER },
                { label: t('client'), value: CompanySearchTypes.TENANT },
                { label: t('linkedInAccount'), value: CompanySearchTypes.LINKEDIN },
              ]}
              value={searchType}
              size={'small'}
              row={true}
              onChange={handleSearchTypeChange}
            />
          </Stack>
          <Stack direction={'row'} alignItems={'center'} gap={2}>
            <Divider orientation={'vertical'} flexItem={true} />
            <Stack direction={'row'} alignItems={'center'} justifyContent={'flex-end'} gap={2}>
              <Typography className={classes.searchTypeLabel}>{t('sorting')}:</Typography>
              <PaiSelect value={sortBy} onChange={handleSortByChange} dense={true} style={{ width: '175px' }}>
                {sortByOptions.map((option) => (
                  <PaiMenuItem key={option.value} value={option.value}>
                    {option.label}
                  </PaiMenuItem>
                ))}
              </PaiSelect>
            </Stack>
          </Stack>
          {isRealAdmin ? (
            <Stack direction={'row'} alignItems={'center'} gap={2}>
              <Divider orientation={'vertical'} flexItem={true} />
              <Stack direction={'row'} alignItems={'center'} justifyContent={'flex-end'} gap={1}>
                <Typography className={classes.searchTypeLabel}>{t('view')}:</Typography>
                <PaiRadioGroup
                  items={[
                    { label: t('user'), value: TenantScopes.USERS },
                    { label: t('license'), value: TenantScopes.LICENSES },
                  ]}
                  value={tenantScope}
                  size={'small'}
                  row={true}
                  onChange={handleToggleTenantScope}
                />
              </Stack>
            </Stack>
          ) : null}
        </Stack>
        <PaiButton text={t('addClient')} size={'small'} border={'rounded'} action={handleOpenCreateTenantDialog} />
      </Stack>
      <Stack ref={tenantsListRef} className={classes.tenants} gap={1}>
        {loading
          ? Array.from({ length: 10 }).map((_, index) => <TenantItemSkeleton key={index} />)
          : tenants.map((tenant) => (
            <TenantItem
              key={tenant.id}
              isRealAdmin={isRealAdmin}
              open={openTenantId === tenant.id}
              tenantScope={tenantScope}
              tenant={tenant}
              tenantUsers={tenantUsers}
              tenantUsersTotal={tenantUsersTotal}
              tenantUsersLoading={tenantUsersLoading}
              tenantUsersItemsPerPage={tenantUsersItemsPerPage}
              tenantUsersPage={tenantUsersPage}
              tenantLicenses={tenantLicenses}
              tenantLicensesTotal={tenantLicensesTotal}
              tenantLicensesLoading={tenantLicensesLoading}
              tenantLicensesPaginationLoading={tenantLicensesPaginationLoading}
              tenantLicensesItemsPerPage={tenantLicensesItemsPerPage}
              tenantLicensesPage={tenantLicensesPage}
              handleOpenTenant={handleOpenTenant(tenant)}
              handleOpenDeleteTenantDialog={handleOpenDeleteTenantDialog(tenant)}
              handleOpenEditTenantDialog={handleOpenEditTenantDialog(tenant)}
              tenantUsersHandleChangePage={tenantUsersHandleChangePage}
              handleOpenCreateUserDialog={handleOpenCreateUserDialog}
              handleOpenEditUserDialog={handleOpenEditUserDialog}
              handleOpenDeleteUserDialog={handleOpenDeleteUserDialog}
              handleOpenAdminSudoLoginDialog={handleOpenAdminSudoLoginDialog}
              tenantLicensesHandleChangePage={tenantLicensesHandleChangePage}
              handleOpenAdminCreateLicenseDialog={handleOpenAdminCreateLicenseDialog}
              handleOpenAdminEditLicenseDialog={handleOpenAdminEditLicenseDialog}
              handleOpenDeleteLicenseDialog={handleOpenDeleteLicenseDialog}
              handleOpenManageUserLicensesDialog={handleOpenManageUserLicensesDialog}
              handleOpenAgencyDialog={handleOpenAgencyDialog(tenant)}
              handleResendInvitation={handleResendInvitation}
              resendingInvitationUserId={resendingInvitationUserId}
            />
          ))}
      </Stack>
      {/*
        Keep clear of the floating chat button. It is fixed at bottom/right 24px with a
        56px diameter and zIndex 1200, so it lands exactly on top of a bottom-right
        pagination control: the page number stayed visible while the "next page" arrow sat
        underneath it, and the list could not be paged at all ("ich kann hier nicht
        blättern, da das Chat-Logo die 2. Seite verdeckt").

        Padding the row rather than moving the button on purpose — the button is global, so
        moving it would only shift the collision onto the next page with something in that
        corner.
      */}
      <Stack direction={'row'} justifyContent={'flex-end'} style={{ marginTop: 'auto', paddingRight: 88 }}>
        <PaiPagination total={total} itemsPerPage={itemsPerPage} page={page} handleChangePage={handleChangePage} hidden={total <= itemsPerPage} />
      </Stack>
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  manageTenants: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    height: '100%',
    background: '#FFF',
    borderRadius: '12px',
    padding: '28px 32px',
    [theme.breakpoints.down('lg')]: {
      padding: '24px',
    },
  },
  blockTitle: {
    fontSize: theme.typography.pxToRem(17),
    fontWeight: 500,
  },
  listHeader: {
    flexWrap: 'wrap',
  },
  tenants: {
    overflow: 'auto',
  },
  searchTypeLabel: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 500,
    color: theme.palette.text.secondary,
  },
  tenantItem: {
    background: '#FFF',
    borderRadius: '8px',
    border: `1px solid ${theme.palette.greys.lighter}`,
    '&.open': {
      border: `1px solid ${theme.palette.primary.main}`,
      '& .expand-icon': {
        color: theme.palette.primary.main,
      },
    },
    '&:hover': {
      border: `1px solid ${theme.palette.primary.main}`,
      '& .expand-icon': {
        color: theme.palette.primary.main,
      },
    },
    '&.skeleton': {
      pointerEvents: 'none',
    },
  },
  tenantItemHeader: {
    cursor: 'pointer',
    padding: '8px 16px',
    '&:hover': {
      background: theme.palette.action.hover,
    },
  },
  expandIcon: {
    transition: 'transform 0.3s ease-in-out',
    transform: 'rotate(0deg)',
    '&.open': {
      transform: 'rotate(180deg)',
    },
  },
  tenantName: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 500,
  },
  tenantDate: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.text.secondary,
  },
  icon: {
    fontSize: theme.typography.pxToRem(24),
    color: theme.palette.text.secondary,
  },
  iconButton: {
    padding: '4px',
    '& .MuiSvgIcon-root': {
      fontSize: theme.typography.pxToRem(20),
      color: theme.palette.greys.medium,
    },
    '&.delete': {
      '& .MuiSvgIcon-root': {
        color: theme.palette.error.main,
      },
    },
  },
  tenantItemContent: {
    padding: '16px',
  },
}));

export default ManageTenants;
